// Safe HTML renderer + sanitiser for Mango Notes rich-text notes.
// Uses DOMPurify (industry-standard, continuously fuzz-tested against XSS
// bypasses) with a strict allowlist of block/inline formatting tags. Any
// output passed to `dangerouslySetInnerHTML` or `element.innerHTML` in this
// app MUST go through `sanitizeHtml` first.

import DOMPurify from "dompurify";

const ALLOWED_TAGS = ["p", "br", "b", "strong", "i", "em", "u", "s", "strike", "ul", "ol", "li", "div", "span"];
// No attributes are needed for note content — strip them all, including
// event handlers and `href`/`src` so a sneaky `<a>` can't appear via nested
// unwrapping. Belt-and-braces: FORBID known attack vectors.
const PURIFY_CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR: [],
  FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "link", "meta", "form", "input", "button", "a", "img"],
  FORBID_ATTR: ["style", "onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur", "href", "src", "srcset"],
  KEEP_CONTENT: true,
  USE_PROFILES: { html: true },
};

/** Convert any raw HTML string into a safe HTML string with only our allowlist. */
export function sanitizeHtml(html) {
  if (!html) return "";
  if (typeof window === "undefined") return "";
  return DOMPurify.sanitize(String(html), PURIFY_CONFIG);
}

/** Convert legacy markdown (`**bold**`, `*italic*`, `- bullet`, `• bullet`) into HTML. */
export function markdownToHtml(src) {
  if (!src) return "";
  if (/<[a-z][^>]*>/i.test(src)) return src; // already HTML
  const lines = src.split("\n");
  const out = [];
  let inList = false;
  const flushList = () => { if (inList) { out.push("</ul>"); inList = false; } };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushList(); out.push("<p><br></p>"); continue; }
    const bullet = line.match(/^[-*•]\s+(.*)$/);
    if (bullet) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push(`<li>${inlineMd(bullet[1])}</li>`);
    } else {
      flushList();
      out.push(`<p>${inlineMd(line)}</p>`);
    }
  }
  flushList();
  return out.join("");
}

function escapeText(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function inlineMd(text) {
  // escape first, then recognise markers
  let s = escapeText(text);
  s = s.replace(/\*\*\*([^*\n]+)\*\*\*/g, "<strong><em>$1</em></strong>");
  s = s.replace(/___([^_\n]+)___/g, "<strong><em>$1</em></strong>");
  s = s.replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/__([^_\n]+)__/g, "<strong>$1</strong>");
  s = s.replace(/\*([^*\n]+)\*/g, "<em>$1</em>");
  s = s.replace(/_([^_\n]+)_/g, "<em>$1</em>");
  return s;
}

/** Returns plain text (no tags) for things like search indexing / previews. */
export function htmlToText(html) {
  if (!html) return "";
  if (typeof window === "undefined") return html;
  const d = new DOMParser().parseFromString(html, "text/html");
  return d.body.textContent || "";
}

/** Wrap selected text inside the given contenteditable element with <strong> / <em> / etc. */
export function execFormat(cmd) {
  if (typeof document === "undefined") return;
  // Use preferred tags
  try { document.execCommand("styleWithCSS", false, false); } catch {}
  document.execCommand(cmd, false, null);
}
